import type { ProjectTimeline, ReelProject, TimelineItem } from '@/types';

/** Sum of each image's on-screen duration (transitions overlap, they don't add time). */
export function imagesDurationSeconds(project: ReelProject): number {
  return project.images.reduce((sum, img) => sum + Math.max(0.1, img.duration), 0);
}

export function voiceoverEffectiveDuration(project: ReelProject): number {
  const v = project.voiceover;
  if (!v.generatedDurationSeconds) return 0;
  const trimmed = Math.max(0, v.generatedDurationSeconds - v.trimStart - v.trimEnd);
  const withSilence = v.silenceBefore + trimmed + v.silenceAfter;
  const end = v.endAt ?? v.startAt + withSilence;
  return Math.max(0, end);
}

/**
 * Computes the total project duration. By default this is driven by the image
 * sequence; if `matchDurationToVoiceover` is enabled, the voiceover's end time
 * (including start offset) wins instead, provided a voiceover has been generated.
 */
export function computeTotalDuration(project: ReelProject): number {
  const imagesDuration = imagesDurationSeconds(project);
  if (project.export.matchDurationToVoiceover) {
    const voDuration = voiceoverEffectiveDuration(project);
    if (voDuration > 0) return voDuration;
  }
  return imagesDuration;
}

const TRACK_COLORS: Record<TimelineItem['track'], string> = {
  image: '#4a5cf7',
  voiceover: '#22c55e',
  caption: '#f59e0b',
  music: '#ec4899',
  watermark: '#64748b',
};

/** Builds the normalized list of TimelineItems the Timeline UI renders. */
export function computeTimeline(project: ReelProject): ProjectTimeline {
  const items: TimelineItem[] = [];
  let cursor = 0;

  for (const img of project.images) {
    const start = cursor;
    const end = cursor + Math.max(0.1, img.duration);
    items.push({
      id: `image-${img.id}`,
      track: 'image',
      label: `Image ${img.order + 1}`,
      startSeconds: start,
      endSeconds: end,
      color: TRACK_COLORS.image,
      refId: img.id,
    });
    cursor = end;
  }

  const totalDuration = computeTotalDuration(project);

  if (project.voiceover.generatedDurationSeconds) {
    const voDuration = voiceoverEffectiveDuration(project);
    items.push({
      id: 'voiceover',
      track: 'voiceover',
      label: 'Voiceover',
      startSeconds: project.voiceover.startAt,
      endSeconds: project.voiceover.startAt + Math.max(0, voDuration - project.voiceover.startAt),
      color: TRACK_COLORS.voiceover,
      refId: 'voiceover',
    });

    if (project.captions.enabled && project.voiceover.wordTimings?.length) {
      items.push({
        id: 'captions',
        track: 'caption',
        label: 'Captions',
        startSeconds: project.voiceover.startAt,
        endSeconds: project.voiceover.startAt + voDuration,
        color: TRACK_COLORS.caption,
        refId: 'captions',
      });
    }
  }

  if (project.music.enabled && project.music.mediaId) {
    const musicEnd = project.music.loop ? totalDuration : Math.min(totalDuration, project.music.startAt + totalDuration);
    items.push({
      id: 'music',
      track: 'music',
      label: 'Music',
      startSeconds: project.music.startAt,
      endSeconds: musicEnd,
      color: TRACK_COLORS.music,
      refId: 'music',
    });
  }

  if (project.watermark.enabled) {
    const start = project.watermark.alwaysVisible ? 0 : project.watermark.startAt;
    const end = project.watermark.alwaysVisible ? totalDuration : project.watermark.endAt ?? totalDuration;
    items.push({
      id: 'watermark',
      track: 'watermark',
      label: 'Watermark',
      startSeconds: start,
      endSeconds: end,
      color: TRACK_COLORS.watermark,
      refId: 'watermark',
    });
  }

  return { totalDurationSeconds: totalDuration, items };
}
